/**
 * Partner Home Screen - Enterprise Grade Dashboard
 * Professional dashboard for partner users to view their business statistics
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Alert,
  TouchableOpacity,
} from 'react-native';
import { useAuthStore, useAuthTokens, reportsService, type PartnerReportsResponse } from '@app/core';
import { Button, Card, Text } from '@app/design-system';
import { colors } from '@app/design-system';

interface PartnerHomeScreenProps {
  onLogout?: () => void;
}

interface MonthlyData {
  month: string;
  year: number;
  count: number;
  monthYear: string;
}

export const PartnerHomeScreen: React.FC<PartnerHomeScreenProps> = ({
  onLogout,
}) => {
  const [partnerData, setPartnerData] = useState<PartnerReportsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const tokens = useAuthTokens();
  const { logout } = useAuthStore();

  // Decode JWT token to extract partner ID from 'sub' field
  const getPartnerIdFromToken = () => {
    try {
      if (!tokens?.oauthToken) {
        console.log('❌ [PartnerHome] No oauth token found');
        return null;
      }

      const parts = tokens.oauthToken.split('.');
      if (parts.length !== 3) {
        console.error('❌ [PartnerHome] Invalid JWT format');
        return null;
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const parsedPayload = JSON.parse(decoded);

      const partnerId = parsedPayload.sub;
      return partnerId;
    } catch (error) {
      console.error('❌ [PartnerHome] Failed to decode JWT token:', error);
      return null;
    }
  };

  const partnerId = getPartnerIdFromToken();

  const loadPartnerData = async () => {
    if (!partnerId) {
      setError('Partner ID (sub) not found in JWT token');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('📡 [PartnerHome] Calling API: GET /api/reports/partner/' + partnerId);
      const data = await reportsService.getPartnerReports(partnerId);

      console.log('✅ [PartnerHome] API Response received:', data);
      setPartnerData(data);
    } catch (error: any) {
      const errorMsg = error?.message || 'Failed to load partner data';
      console.error('❌ [PartnerHome] API Error:', error);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartnerData();
  }, [partnerId]);

  const handleLogout = async () => {
    try {
      await logout();
      onLogout?.();
    } catch (error) {
      console.error('❌ [PartnerHome] Logout error:', error);
      Alert.alert('Error', 'Failed to logout');
    }
  };

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to get month-year from ObjectID or date string
  const getMonthYearFromDate = (dateString: string) => {
    try {
      let date: Date;

      // If it's an ObjectID (24 hex chars), extract timestamp
      if (dateString.match(/^[0-9a-fA-F]{24}$/)) {
        const timestamp = parseInt(dateString.substring(0, 8), 16) * 1000;
        date = new Date(timestamp);
      } else {
        date = new Date(dateString);
      }

      return {
        month: date.toLocaleDateString('en-US', { month: 'short' }),
        year: date.getFullYear(),
        monthYear: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      };
    } catch {
      return { month: 'Unknown', year: 2026, monthYear: 'Unknown' };
    }
  };

  // Calculate monthly shop enrollments
  const getMonthlyShopData = (): MonthlyData[] => {
    if (!partnerData?.shops) return [];

    const monthlyMap = new Map<string, MonthlyData>();

    partnerData.shops.forEach(shop => {
      const dateInfo = getMonthYearFromDate(shop.creationDate);
      const key = dateInfo.monthYear;

      if (monthlyMap.has(key)) {
        monthlyMap.get(key)!.count += 1;
      } else {
        monthlyMap.set(key, {
          month: dateInfo.month,
          year: dateInfo.year,
          count: 1,
          monthYear: dateInfo.monthYear
        });
      }
    });

    return Array.from(monthlyMap.values()).sort((a, b) =>
      new Date(a.monthYear).getTime() - new Date(b.monthYear).getTime()
    );
  };

  // Calculate monthly subscription sales
  const getMonthlySubscriptionData = (): MonthlyData[] => {
    if (!partnerData?.shops) return [];

    const monthlyMap = new Map<string, MonthlyData>();

    partnerData.shops.forEach(shop => {
      shop.subscriptions?.forEach(subscription => {
        const dateInfo = getMonthYearFromDate(subscription.createdAt);
        const key = dateInfo.monthYear;

        if (monthlyMap.has(key)) {
          monthlyMap.get(key)!.count += 1;
        } else {
          monthlyMap.set(key, {
            month: dateInfo.month,
            year: dateInfo.year,
            count: 1,
            monthYear: dateInfo.monthYear
          });
        }
      });
    });

    return Array.from(monthlyMap.values()).sort((a, b) =>
      new Date(a.monthYear).getTime() - new Date(b.monthYear).getTime()
    );
  };

  // Sort shops by active status (active first)
  const getSortedShops = () => {
    if (!partnerData?.shops) return [];

    return [...partnerData.shops].sort((a, b) => {
      const aHasActive = a.subscriptions?.some(sub => sub.isActive) || false;
      const bHasActive = b.subscriptions?.some(sub => sub.isActive) || false;

      if (aHasActive && !bHasActive) return -1;
      if (!aHasActive && bHasActive) return 1;
      return 0;
    });
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading partner dashboard...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <Card style={styles.errorCard}>
          <Text variant="h3" style={styles.errorTitle}>❌ Error</Text>
          <Text style={styles.errorText}>{error}</Text>
          <Button onPress={loadPartnerData} style={styles.retryButton}>
            Retry
          </Button>
        </Card>
      </View>
    );
  }

  const monthlyShops = getMonthlyShopData();
  const monthlySubscriptions = getMonthlySubscriptionData();
  const sortedShops = getSortedShops();

  return (
    <View style={styles.container}>
      {/* Header with Welcome Message */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <Text variant="h1" bold style={styles.welcomeText}>
            Welcome Business Partner
          </Text>
          <Text variant="h3" style={styles.partnerIdText}>
            {partnerId}
          </Text>
        </View>
        <TouchableOpacity onPress={handleLogout} style={styles.logoutButton}>
          <Text style={styles.logoutText}>Logout</Text>
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Key Statistics */}
        <View style={styles.statsContainer}>
          <View style={styles.statsGrid}>
            <Card style={styles.statCard}>
              <Text style={styles.statNumber}>{partnerData?.totalShops || 0}</Text>
              <Text style={styles.statLabel}>Total Shops You Have Enrolled</Text>
            </Card>

            <Card style={styles.statCard}>
              <Text style={styles.statNumber}>{partnerData?.totalSubscriptions || 0}</Text>
              <Text style={styles.statLabel}>Total Subscriptions You Sold</Text>
            </Card>
          </View>
        </View>

        {/* Monthly Shop Enrollments */}
        <Card style={styles.chartCard}>
          <Text variant="h2" bold style={styles.chartTitle}>
            📊 Monthly Shop Enrollments
          </Text>

          {monthlyShops.length > 0 ? (
            <View style={styles.chartContainer}>
              {monthlyShops.map((item, index) => (
                <View key={index} style={styles.chartBar}>
                  <View style={styles.barInfo}>
                    <Text style={styles.barLabel}>{item.monthYear}</Text>
                    <Text style={styles.barValue}>{item.count}</Text>
                  </View>
                  <View style={styles.barContainer}>
                    <View
                      style={[
                        styles.bar,
                        {
                          width: `${(item.count / Math.max(...monthlyShops.map(m => m.count))) * 100}%`,
                          backgroundColor: colors.primary
                        }
                      ]}
                    />
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.noDataText}>No shop enrollment data available</Text>
          )}
        </Card>

        {/* Monthly Subscription Sales */}
        <Card style={styles.chartCard}>
          <Text variant="h2" bold style={styles.chartTitle}>
            💰 Monthly Subscription Sales
          </Text>

          {monthlySubscriptions.length > 0 ? (
            <View style={styles.chartContainer}>
              {monthlySubscriptions.map((item, index) => (
                <View key={index} style={styles.chartBar}>
                  <View style={styles.barInfo}>
                    <Text style={styles.barLabel}>{item.monthYear}</Text>
                    <Text style={styles.barValue}>{item.count}</Text>
                  </View>
                  <View style={styles.barContainer}>
                    <View
                      style={[
                        styles.bar,
                        {
                          width: `${(item.count / Math.max(...monthlySubscriptions.map(m => m.count))) * 100}%`,
                          backgroundColor: colors.success
                        }
                      ]}
                    />
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.noDataText}>No subscription sales data available</Text>
          )}
        </Card>

        {/* Shop Listings */}
        <Card style={styles.shopsCard}>
          <Text variant="h2" bold style={styles.sectionTitle}>
            🏪 Your Enrolled Shops ({sortedShops.length})
          </Text>

          {sortedShops.length > 0 ? (
            sortedShops.map((shop, index) => {
              const hasActiveSubscription = shop.subscriptions?.some(sub => sub.isActive) || false;

              return (
                <View key={shop.shopId} style={[
                  styles.shopItem,
                  hasActiveSubscription ? styles.activeShop : styles.inactiveShop
                ]}>
                  <View style={styles.shopHeader}>
                    <View style={styles.shopNameContainer}>
                      <Text style={styles.shopName}>{shop.shopName}</Text>
                      <View style={[
                        styles.statusBadge,
                        hasActiveSubscription ? styles.activeBadge : styles.inactiveBadge
                      ]}>
                        <Text style={styles.statusText}>
                          {hasActiveSubscription ? '● ACTIVE' : '● INACTIVE'}
                        </Text>
                      </View>
                    </View>
                  </View>

                  <Text style={styles.shopOwner}>Owner: {shop.ownerName}</Text>
                  <Text style={styles.shopAddress}>📍 {shop.address}</Text>
                  <Text style={styles.shopDate}>
                    Created: {formatDate(shop.creationDate)}
                  </Text>

                  {/* Shop Subscriptions */}
                  {shop.subscriptions && shop.subscriptions.length > 0 && (
                    <View style={styles.subscriptionsSection}>
                      <Text style={styles.subscriptionsTitle}>
                        Subscriptions ({shop.subscriptions.length})
                      </Text>

                      {shop.subscriptions.map((sub, subIndex) => (
                        <View key={sub.subscriptionId} style={styles.subscriptionItem}>
                          <View style={styles.subscriptionHeader}>
                            <Text style={styles.planName}>
                              {sub.planName || 'Unknown Plan'}
                            </Text>
                            <View style={[
                              styles.subStatusBadge,
                              sub.isActive ? styles.activeSubBadge : styles.inactiveSubBadge
                            ]}>
                              <Text style={styles.subStatusText}>
                                {sub.isActive ? 'ACTIVE' : 'EXPIRED'}
                              </Text>
                            </View>
                          </View>

                          <Text style={styles.subscriptionDates}>
                            📅 {formatDate(sub.planStartDate)} - {formatDate(sub.planEndDate)}
                          </Text>
                          <Text style={styles.subscriptionCreated}>
                            Sold: {formatDate(sub.createdAt)}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              );
            })
          ) : (
            <Text style={styles.noDataText}>No shops enrolled yet</Text>
          )}
        </Card>

        {/* Refresh Button */}
        <View style={styles.refreshContainer}>
          <Button onPress={loadPartnerData} style={styles.refreshButton}>
            🔄 Refresh Dashboard
          </Button>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.gray1,
  },

  // Loading States
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.gray7,
  },

  // Error States
  errorCard: {
    margin: 20,
    padding: 24,
    alignItems: 'center',
  },
  errorTitle: {
    color: colors.error,
    marginBottom: 12,
  },
  errorText: {
    textAlign: 'center',
    marginBottom: 20,
    color: colors.gray7,
  },
  retryButton: {
    marginTop: 12,
  },

  // Header Section
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 20,
    paddingTop: 50, // Status bar spacing
  },
  headerContent: {
    flex: 1,
  },
  welcomeText: {
    color: colors.white,
    fontSize: 24,
    marginBottom: 4,
  },
  partnerIdText: {
    color: colors.white,
    fontSize: 16,
    opacity: 0.9,
  },
  logoutButton: {
    backgroundColor: colors.white,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  logoutText: {
    color: colors.primary,
    fontWeight: 'bold',
    fontSize: 14,
  },

  // Content Area
  content: {
    flex: 1,
  },

  // Statistics Section
  statsContainer: {
    padding: 20,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  statCard: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
    backgroundColor: colors.white,
  },
  statNumber: {
    fontSize: 36,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 14,
    textAlign: 'center',
    color: colors.gray7,
    fontWeight: '500',
    lineHeight: 18,
  },

  // Chart Cards
  chartCard: {
    margin: 20,
    marginTop: 0,
    padding: 20,
  },
  chartTitle: {
    color: colors.gray9,
    marginBottom: 16,
    fontSize: 18,
  },
  chartContainer: {
    gap: 12,
  },
  chartBar: {
    marginBottom: 12,
  },
  barInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  barLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.gray8,
  },
  barValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
  },
  barContainer: {
    height: 12,
    backgroundColor: colors.gray2,
    borderRadius: 6,
    overflow: 'hidden',
  },
  bar: {
    height: '100%',
    borderRadius: 6,
    minWidth: 4,
  },
  noDataText: {
    textAlign: 'center',
    color: colors.gray6,
    fontStyle: 'italic',
    padding: 20,
  },

  // Shops Section
  shopsCard: {
    margin: 20,
    marginTop: 0,
    padding: 20,
  },
  sectionTitle: {
    color: colors.gray9,
    marginBottom: 20,
    fontSize: 18,
  },

  // Shop Items
  shopItem: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 2,
  },
  activeShop: {
    borderColor: colors.success,
    backgroundColor: colors.successLight,
  },
  inactiveShop: {
    borderColor: colors.gray3,
    backgroundColor: colors.gray1,
  },
  shopHeader: {
    marginBottom: 12,
  },
  shopNameContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  shopName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.gray9,
    flex: 1,
    marginRight: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  activeBadge: {
    backgroundColor: colors.success,
  },
  inactiveBadge: {
    backgroundColor: colors.gray5,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.white,
  },
  shopOwner: {
    fontSize: 14,
    color: colors.gray8,
    marginBottom: 4,
  },
  shopAddress: {
    fontSize: 14,
    color: colors.gray6,
    marginBottom: 4,
  },
  shopDate: {
    fontSize: 12,
    color: colors.gray5,
    marginBottom: 12,
  },

  // Subscriptions Section
  subscriptionsSection: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.gray3,
  },
  subscriptionsTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.gray8,
    marginBottom: 8,
  },
  subscriptionItem: {
    backgroundColor: colors.gray2,
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  subscriptionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  planName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray9,
    flex: 1,
    marginRight: 8,
  },
  subStatusBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  activeSubBadge: {
    backgroundColor: colors.success,
  },
  inactiveSubBadge: {
    backgroundColor: colors.error,
  },
  subStatusText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.white,
  },
  subscriptionDates: {
    fontSize: 12,
    color: colors.gray7,
    marginBottom: 2,
  },
  subscriptionCreated: {
    fontSize: 12,
    color: colors.gray6,
  },

  // Refresh Section
  refreshContainer: {
    padding: 20,
    paddingBottom: 40,
  },
  refreshButton: {
    backgroundColor: colors.primary,
  },
});

